import * as React from "react";
import { Building2, MapPin } from "lucide-react";
import type { Pagination as PageMeta, SiteSummary } from "@taskiq/shared";
import { FilterSelect, type FilterOption } from "@/components/ui/FilterSelect";
import { api } from "@/lib/api";
import { useCompanySites } from "@/lib/scope-lists";
import { useTenancy } from "@/lib/tenancy";
import { useApi } from "@/lib/useApi";

interface CompanyList {
  companies: { id: string; name: string; is_active: boolean }[];
  pagination: PageMeta;
}

interface SiteList {
  sites: (SiteSummary & { company_id: number })[];
  pagination: PageMeta;
}

export function ScopeSwitcher() {
  const { activeCompanyId, activeSiteId, canSwitchCompany, homeCompany, homeSite, setActiveScope } =
    useTenancy();

  const companies = useApi<CompanyList>(canSwitchCompany ? "/companies?pageSize=100" : null);

  const sites = useCompanySites(activeCompanyId);

  // ── Deactivated companies and sites are not offered here ────────────────
  //
  // Not cosmetic, and not this component's own rule either - it is the UI
  // finally agreeing with the database. `app.can_see_site` returns false for an
  // inactive site *for every role including superadmin*, and every `app.*`
  // helper returns NULL for a member of an inactive company, so switching into
  // one produced a scope in which every query legitimately returned nothing:
  // an empty Schedule, an empty Team, no error to explain it.
  //
  // Filtered here rather than in `useCompanySites`, which the Overview and the
  // dashboard also read, and rather than with `is_active=true` on the request,
  // which would give the switcher and the Sites screen two different cache
  // entries for the same question. The Sites and Companies screens still list
  // inactive rows, because reactivating one is done from there.
  const companyOptions: FilterOption[] = React.useMemo(() => {
    const rows = companies.data?.companies.filter((company) => company.is_active);
    if (rows && rows.length > 0) {
      return rows.map((company) => ({ value: String(company.id), label: company.name }));
    }
    // The fallback is the reader's own company off their profile, which is the
    // only row an admin ever has. Kept even if it is inactive: a chip that
    // names where you are is not the same as an option you may move to, and
    // blanking it strands somebody whose tenant was just suspended on a
    // switcher that names nothing.
    return homeCompany ? [{ value: String(homeCompany.id), label: homeCompany.name }] : [];
  }, [companies.data, homeCompany]);

  const siteOptions: FilterOption[] = React.useMemo(() => {
    const rows = sites.data?.sites.filter((site) => site.is_active);
    if (rows && rows.length > 0) {
      return rows.map((site) => ({ value: String(site.id), label: site.name }));
    }
    return homeSite ? [{ value: String(homeSite.id), label: homeSite.name }] : [];
  }, [sites.data, homeSite]);

  // A stored scope pointing somewhere unreachable falls back to the reader's
  // home site. `is_active` is part of "reachable": a yard closed while
  // somebody had it selected leaves a scope that reads as empty rather than as
  // closed, and the stored value outlives the session that set it.
  React.useEffect(() => {
    if (!sites.data || !homeSite || activeSiteId === null) return;
    if (sites.data.sites.some((site) => site.id === activeSiteId && site.is_active)) return;

    setActiveScope({ companyId: homeCompany?.id ?? null, siteId: homeSite.id });
  }, [sites.data, activeSiteId, homeSite, homeCompany, setActiveScope]);

  async function onCompanyChange(companyId: string) {
    const list = await fetchSites(companyId);
    // The first *active* site. Landing on a closed one is the same dead scope
    // the options above now exclude, reached by a different route.
    const first = list.find((site) => site.is_active);
    if (!first) return;

    setActiveScope({ companyId, siteId: first.id });
  }

  return (
    <div className="scope">
      <FilterSelect
        variant="chip"
        label="Workspace"
        icon={<Building2 />}
        value={String(activeCompanyId ?? "")}
        options={companyOptions}
        searchable={false}
        onChange={(value) => void onCompanyChange(value)}
      />

      <FilterSelect
        variant="chip"
        label="Location"
        icon={<MapPin />}
        value={activeSiteId === null ? "" : String(activeSiteId)}
        options={siteOptions}
        searchable={false}
        onChange={(value) => setActiveScope({ companyId: activeCompanyId, siteId: value })}
      />
    </div>
  );
}

async function fetchSites(companyId: string): Promise<SiteSummary[]> {
  const result = await api<SiteList>(`/sites?pageSize=100&company_id=${companyId}`);
  return result.sites;
}
